/**
 * Way2Buy24 – Warenkorb / Anfrageliste v1.4.0
 * Synchronisiert Mengen vor Checkout oder Anfrage serverseitig.
 */
(function (W2B) {
  'use strict';

  const rootPath = () => window.Shopify?.routes?.root || '/';

  function showFormMessage(form, message, type = 'error') {
    let box = form.querySelector('[data-cart-form-message]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'cart-b2b-panel__message';
      box.setAttribute('data-cart-form-message', '');
      form.prepend(box);
    }
    box.setAttribute('role', type === 'error' ? 'alert' : 'status');
    box.classList.toggle('cart-b2b-panel__message--error', type === 'error');
    box.classList.toggle('cart-b2b-panel__message--success', type === 'success');
    box.textContent = message;
    box.hidden = false;
  }

  function clearFormMessage(form) {
    const box = form.querySelector('[data-cart-form-message]');
    if (box) box.hidden = true;
  }

  function validateCheckoutForm(form) {
    const guestFields = form.querySelector('[data-cart-guest-fields]');
    if (!guestFields) return true;
    const findField = (...names) => names.map((name) => guestFields.querySelector(`[name="${name}"]`)).find(Boolean);
    const required = [
      { el: findField('attributes[Firma]', 'contact[Firma]'), label: 'Firma' },
      { el: findField('attributes[Ansprechpartner]', 'contact[name]'), label: 'Ansprechpartner' },
      { el: findField('attributes[E-Mail]', 'contact[email]'), label: 'E-Mail' },
    ];
    for (const field of required) {
      if (!field.el) continue;
      if (!field.el.value.trim()) {
        showFormMessage(form, `Bitte füllen Sie das Pflichtfeld „${field.label}“ aus.`);
        field.el.focus();
        return false;
      }
    }
    const email = findField('attributes[E-Mail]', 'contact[email]');
    if (email?.value && !email.checkValidity()) {
      showFormMessage(form, 'Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      email.focus();
      return false;
    }
    return true;
  }

  function normaliseQty(input) {
    const min = Math.max(1, parseInt(input.min, 10) || 1);
    const max = input.hasAttribute('max') ? parseInt(input.max, 10) : null;
    let value = parseInt(input.value, 10);
    if (!Number.isFinite(value) || value < min) value = min;
    if (Number.isFinite(max)) value = Math.min(value, max);
    input.value = String(value);
    return value;
  }

  function cartInputs() {
    return Array.from(document.querySelectorAll('#CartB2BForm [data-cart-qty]'));
  }

  function hasQuantityChanges() {
    return cartInputs().some((input) => String(normaliseQty(input)) !== input.dataset.initialQuantity);
  }

  async function syncCartQuantities(extra = {}) {
    const inputs = cartInputs();
    const updates = {};
    inputs.forEach((input) => {
      const key = input.dataset.lineKey;
      if (key) updates[key] = normaliseQty(input);
    });

    const response = await fetch(`${rootPath()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, ...extra }),
    });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(cart.description || cart.message || 'Der Warenkorb konnte nicht aktualisiert werden.');

    for (const input of inputs) {
      const key = input.dataset.lineKey;
      const line = cart.items?.find((item) => item.key === key);
      if (!line || Number(line.quantity) !== Number(input.value)) {
        throw new Error('Eine Menge konnte nicht vollständig übernommen werden. Bitte prüfen Sie den verfügbaren Lagerbestand.');
      }
      input.dataset.initialQuantity = String(line.quantity);
    }
    return cart;
  }

  function collectCheckoutData(form) {
    const attributes = {};
    let note = '';
    new FormData(form).forEach((value, name) => {
      const match = name.match(/^attributes\[(.+)]$/);
      if (match) attributes[match[1]] = String(value);
      if (name === 'note') note = String(value);
    });
    return { attributes, note };
  }

  function refreshInquiryQuantityText(form) {
    const field = form.querySelector('textarea[name="contact[Anfrageliste]"]');
    if (!field) return;
    const quantities = cartInputs().map((input) => normaliseQty(input));
    let index = 0;
    field.value = field.value.replace(/Menge:\s*\d+/g, () => `Menge: ${quantities[index++] ?? 1}`);
  }

  function setBusy(form, busy) {
    form.querySelectorAll('button[type="submit"]').forEach((button) => {
      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    });
  }

  function initCartQtyForm() {
    const form = document.getElementById('CartB2BForm');
    const updateBtn = form?.querySelector('[data-cart-update]');
    if (!form || !updateBtn || form.dataset.qtyBound === 'true') return;
    form.dataset.qtyBound = 'true';

    const formatMoney = (cents) => {
      if (typeof W2B.formatMoney === 'function') return W2B.formatMoney(cents / 100);
      const locale = document.body?.dataset.shopLocale || document.documentElement.lang || 'de-DE';
      const currency = document.body?.dataset.shopCurrency || 'EUR';
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
    };
    const vatRate = Math.max(0, Number(document.body?.dataset.displayVatRate || 19)) / 100;
    const updateVisibleTotals = () => {
      let netTotal = 0;
      let itemCount = 0;
      form.querySelectorAll('[data-cart-line]').forEach((line) => {
        const input = line.querySelector('[data-cart-qty]');
        const qty = parseInt(input?.value, 10) || 0;
        const unit = parseInt(line.dataset.unitPriceCents, 10) || 0;
        const total = qty * unit;
        itemCount += qty;
        netTotal += total;
        line.querySelectorAll('[data-cart-display-qty], [data-cart-price-qty]').forEach((el) => { el.textContent = String(qty); });
        const totalEl = line.querySelector('[data-cart-line-total]');
        if (totalEl) totalEl.textContent = formatMoney(total);
      });
      const meta = document.querySelector('.cart-page__meta span:last-child');
      if (meta) meta.textContent = itemCount === 1 ? '1 Gerät' : `${itemCount} Geräte gesamt`;
      const vat = Math.round(netTotal * vatRate);
      const netEl = document.querySelector('[data-cart-net-total]');
      const vatEl = document.querySelector('[data-cart-vat-total]');
      const grossEl = document.querySelector('[data-cart-gross-total]');
      if (netEl) netEl.textContent = formatMoney(netTotal);
      if (vatEl) vatEl.textContent = formatMoney(vat);
      if (grossEl) grossEl.textContent = formatMoney(netTotal + vat);
    };

    cartInputs().forEach((input) => {
      input.dataset.initialQuantity = String(input.value);
      const onChange = () => {
        normaliseQty(input);
        updateVisibleTotals();
        updateBtn.hidden = !hasQuantityChanges();
      };
      input.addEventListener('change', onChange);
      input.addEventListener('input', () => { updateVisibleTotals(); updateBtn.hidden = !hasQuantityChanges(); });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      clearFormMessage(form);
      setBusy(form, true);
      try {
        await syncCartQuantities();
        window.location.reload();
      } catch (error) {
        showFormMessage(form, error.message || 'Der Warenkorb konnte nicht aktualisiert werden.');
        setBusy(form, false);
      }
    });
  }

  function initCartCheckoutForm() {
    const form = document.querySelector('.cart-b2b-checkout-form');
    if (!form || form.dataset.checkoutBound === 'true') return;
    form.dataset.checkoutBound = 'true';

    form.addEventListener('submit', async (event) => {
      if (form.dataset.nativeSubmit === 'true') return;
      event.preventDefault();
      const submitter = event.submitter;
      const cartMode = form.dataset.cartMode || document.querySelector('[data-cart-mode]')?.dataset.cartMode || 'inquiry';
      clearFormMessage(form);

      if (cartMode !== 'checkout' && submitter?.name !== 'update' && !validateCheckoutForm(form)) return;
      setBusy(form, true);

      try {
        if (cartMode === 'checkout' && submitter?.name === 'checkout') {
          await syncCartQuantities(collectCheckoutData(form));
          window.location.assign(`${rootPath()}checkout`);
          return;
        }

        if (cartMode !== 'checkout') {
          await syncCartQuantities();
          refreshInquiryQuantityText(form);

          // Den zweiten Submit regulär auslösen, damit Shopifys CAPTCHA-/Spam-Schutz
          // den Token unmittelbar vor dem Versand erzeugen und validieren kann.
          // HTMLFormElement.prototype.submit() umgeht diese Submit-Handler und kann
          // dadurch bei einzelnen Browsern zu „Invalid CAPTCHA token“ führen.
          form.dataset.nativeSubmit = 'true';
          setBusy(form, false);
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter || undefined);
          } else if (submitter && typeof submitter.click === 'function') {
            submitter.click();
          } else {
            form.submit();
          }
          return;
        }
      } catch (error) {
        showFormMessage(form, error.message || 'Die Mengen konnten nicht gespeichert werden.');
        setBusy(form, false);
      }
    });

    form.querySelectorAll('input, textarea').forEach((field) => field.addEventListener('input', () => clearFormMessage(form)));
  }

  W2B.initCartB2B = function initCartB2B() {
    initCartQtyForm();
    initCartCheckoutForm();
  };
})(window.W2B = window.W2B || {});

/** Sprint 4 – druckfertiger Angebotsentwurf und optionale Speicherung als Shopify-Bestellentwurf. */
(function () {
  'use strict';

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const money = (cents) => {
    const locale = document.body?.dataset.shopLocale || document.documentElement.lang || 'de-DE';
    const currency = document.body?.dataset.shopCurrency || 'EUR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  };
  const dateText = (date) => new Intl.DateTimeFormat(document.documentElement.lang || 'de-DE').format(date);
  const localQuoteNumber = () => {
    const d = new Date();
    const stamp = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('');
    const sequence = String(Math.floor(Math.random() * 99) + 1).padStart(2, '0');
    return `W2B24 - ${stamp}${sequence}`;
  };

  function setQuoteMessage(builder, text, type = 'error') {
    const message = builder.querySelector('[data-quote-message]');
    if (!message) return;
    message.textContent = text || '';
    message.hidden = !text;
    message.classList.toggle('cart-b2b-panel__message--error', type === 'error');
    message.classList.toggle('cart-b2b-panel__message--success', type === 'success');
  }

  function readQuoteFields(builder) {
    const read = (name) => builder.querySelector(`[data-quote-field="${name}"]`)?.value.trim() || '';
    return {
      company: read('company'),
      contact: read('contact'),
      email: read('email'),
      address: read('address'),
      customerReference: read('customerReference'),
    };
  }

  function validateQuoteFields(builder, fields) {
    for (const [key, label] of [['company', 'Firma'], ['contact', 'Ansprechpartner'], ['email', 'E-Mail'], ['address', 'Rechnungsanschrift']]) {
      if (!fields[key]) {
        setQuoteMessage(builder, `Bitte füllen Sie das Pflichtfeld „${label}“ aus.`);
        builder.querySelector(`[data-quote-field="${key}"]`)?.focus();
        return false;
      }
    }
    const emailInput = builder.querySelector('[data-quote-field="email"]');
    if (emailInput && !emailInput.checkValidity()) {
      setQuoteMessage(builder, 'Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      emailInput.focus();
      return false;
    }
    return true;
  }

  function setButtonsBusy(builder, busy) {
    builder.querySelectorAll('[data-create-quote], [data-save-quote]').forEach((button) => {
      button.disabled = busy;
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }

  async function syncCartQuantities() {
    const cartForm = document.getElementById('CartB2BForm');
    if (!cartForm) return;
    const quantityInputs = Array.from(cartForm.querySelectorAll('[data-cart-qty]'));
    if (!quantityInputs.some((input) => input.dataset.initialQuantity !== input.value)) return;

    const updates = {};
    quantityInputs.forEach((input) => {
      if (input.dataset.lineKey) updates[input.dataset.lineKey] = Math.max(1, parseInt(input.value, 10) || 1);
    });
    const response = await fetch(`${rootPath()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Die Mengen konnten nicht gespeichert werden.');
  }

  async function persistQuoteAttributes(fields, extraAttributes = {}) {
    const attributes = {
      'Angebot Firma': fields.company,
      'Angebot Ansprechpartner': fields.contact,
      'Angebot E-Mail': fields.email,
      'Angebot Anschrift': fields.address,
      'Angebot Kundenreferenz': fields.customerReference,
      ...extraAttributes,
    };
    const response = await fetch(`${rootPath()}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Die Angebotsdaten konnten nicht gespeichert werden.');
  }

  async function readCart() {
    const response = await fetch(`${rootPath()}cart.js`, { headers: { Accept: 'application/json' } });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok || !Array.isArray(cart.items)) throw new Error('Der Warenkorb konnte nicht geladen werden.');
    return cart;
  }

  function currentQuantities() {
    const map = new Map();
    document.querySelectorAll('#CartB2BForm [data-cart-qty]').forEach((input) => {
      map.set(input.dataset.lineKey, Math.max(1, parseInt(input.value, 10) || 1));
    });
    return map;
  }

  function fillDocument(doc, fields, suppliedQuoteNumber = '') {
    Object.entries(fields).forEach(([key, value]) => {
      doc.querySelectorAll(`[data-quote-output="${key}"]`).forEach((node) => { node.textContent = value || '–'; });
    });
    const now = new Date();
    const valid = new Date(now);
    valid.setDate(valid.getDate() + Math.max(1, parseInt(doc.dataset.validDays, 10) || 14));
    const generatedQuoteNumber = suppliedQuoteNumber || localQuoteNumber();
    doc.querySelector('[data-quote-output="quoteNumber"]').textContent = generatedQuoteNumber;
    doc.querySelector('[data-quote-output="date"]').textContent = dateText(now);
    doc.querySelector('[data-quote-output="validUntil"]').textContent = dateText(valid);

    const qtyMap = currentQuantities();
    let net = 0;
    doc.querySelectorAll('[data-quote-line]').forEach((row) => {
      const qty = qtyMap.get(row.dataset.quoteLine) || 1;
      const unit = parseInt(row.dataset.unitPriceCents, 10) || 0;
      const total = qty * unit;
      net += total;
      row.querySelector('[data-quote-qty]').textContent = String(qty);
      row.querySelector('[data-quote-line-total]').textContent = money(total);
    });
    const vatRate = Math.max(0, Number(document.body?.dataset.displayVatRate || 19)) / 100;
    const vat = Math.round(net * vatRate);
    doc.querySelector('[data-quote-net]').textContent = money(net);
    doc.querySelectorAll('[data-quote-net-inline]').forEach((node) => { node.textContent = money(net); });
    doc.querySelector('[data-quote-vat]').textContent = money(vat);
    doc.querySelector('[data-quote-gross]').textContent = money(net + vat);
    return generatedQuoteNumber;
  }

  function buildSavePayload(cart, fields, doc) {
    const requestId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return {
      requestId,
      customer: fields,
      cart: {
        token: cart.token || '',
        currency: cart.currency || document.body?.dataset.shopCurrency || 'EUR',
        totalPriceCents: Number(cart.total_price || 0),
        itemCount: Number(cart.item_count || 0),
        items: cart.items.map((item) => ({
          key: item.key,
          variantId: item.variant_id,
          productId: item.product_id,
          quantity: item.quantity,
          title: item.product_title || item.title,
          variantTitle: item.variant_title || '',
          sku: item.sku || '',
          finalPriceCents: item.final_price,
          finalLinePriceCents: item.final_line_price,
          properties: item.properties || {},
          url: item.url || '',
        })),
      },
      offer: {
        prefix: 'W2B24 - ',
        validDays: Math.max(1, parseInt(doc.dataset.validDays, 10) || 14),
        sourceUrl: window.location.href,
      },
    };
  }

  async function saveQuote(builder, doc, fields) {
    const endpoint = (builder.dataset.quoteSaveEndpoint || '').trim();
    if (!endpoint) throw new Error('Die Speicherung ist noch nicht mit einem gesicherten Shopify-Endpunkt verbunden.');
    const cart = await readCart();
    if (!cart.items.length) throw new Error('Der Warenkorb ist leer.');

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buildSavePayload(cart, fields, doc)),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.success === false) {
      throw new Error(data.message || data.error || 'Das Angebot konnte nicht im Shopify-Admin gespeichert werden.');
    }
    if (!data.quoteNumber) throw new Error('Der Speicher-Endpunkt hat keine Angebotsnummer zurückgegeben.');
    return data;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function printStylesMarkup() {
    return Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
      .map((node) => {
        if (node.tagName === 'LINK') {
          return `<link rel="stylesheet" href="${escapeHtml(node.href)}">`;
        }
        return node.outerHTML;
      })
      .join('\n');
  }

  function waitForPrintAssets(printWindow) {
    const images = Array.from(printWindow.document.images || []);
    const imageTasks = images.map((image) => {
      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    });
    const stylesheets = Array.from(printWindow.document.querySelectorAll('link[rel="stylesheet"]'));
    const stylesheetTasks = stylesheets.map((stylesheet) => {
      if (stylesheet.sheet) return Promise.resolve();
      return new Promise((resolve) => {
        stylesheet.addEventListener('load', resolve, { once: true });
        stylesheet.addEventListener('error', resolve, { once: true });
      });
    });
    const fontsReady = printWindow.document.fonts?.ready || Promise.resolve();
    const allReady = Promise.all([...imageTasks, ...stylesheetTasks, fontsReady]);
    const timeout = new Promise((resolve) => window.setTimeout(resolve, 3500));
    return Promise.race([allReady, timeout]);
  }

  function openQuotePrintWindow(doc, fields, quoteNumber = '') {
    const generatedQuoteNumber = fillDocument(doc, fields, quoteNumber);
    const safeFileName = `Angebot-${generatedQuoteNumber.replace(/\s*[-–—]\s*/g, '-').replace(/\s+/g, '-')}`;

    // Ein eigenes Druckfenster funktioniert auch in der Shopify-Theme-Vorschau,
    // in der window.print() innerhalb des Vorschau-iFrames häufig blockiert wird.
    // Das Fenster wird synchron aus dem Klick heraus geöffnet, damit Popup-Schutz
    // und Browser-Sicherheitsrichtlinien den Vorgang nicht unterbinden.
    const printWindow = window.open('', '_blank', 'width=1100,height=900');
    if (!printWindow) {
      throw new Error('Das PDF-Fenster wurde vom Browser blockiert. Bitte Pop-ups für diesen Shop erlauben und erneut klicken.');
    }

    const printableDocument = doc.cloneNode(true);
    printableDocument.removeAttribute('aria-hidden');
    printableDocument.setAttribute('aria-hidden', 'false');

    const locale = document.documentElement.lang || 'de';
    const bodyData = [
      `data-shop-locale="${escapeHtml(document.body?.dataset.shopLocale || locale)}"`,
      `data-shop-currency="${escapeHtml(document.body?.dataset.shopCurrency || 'EUR')}"`,
      `data-display-vat-rate="${escapeHtml(document.body?.dataset.displayVatRate || '19')}"`,
    ].join(' ');

    const popupCss = `
      html,body{margin:0;min-height:100%;background:#eef1f5;color:#1a1a1a}
      body{padding:24px}
      .w2b-quote-print-toolbar{position:sticky;top:0;z-index:9999;display:flex;align-items:center;justify-content:space-between;gap:16px;max-width:210mm;margin:0 auto 18px;padding:12px 14px;border:1px solid #d8dee8;border-radius:8px;background:#fff;box-shadow:0 4px 18px rgba(16,24,40,.12);font:14px/1.4 Arial,Helvetica,sans-serif}
      .w2b-quote-print-toolbar p{margin:0}
      .w2b-quote-print-toolbar button{flex:0 0 auto;border:0;border-radius:6px;padding:10px 16px;background:#a40070;color:#fff;font:600 14px/1.2 Arial,Helvetica,sans-serif;cursor:pointer}
      .w2b-quote-document{display:block!important;position:relative!important;width:210mm!important;max-width:100%!important;min-height:297mm;margin:0 auto!important;padding:8mm 14mm!important;background:#fff!important;box-shadow:0 8px 28px rgba(16,24,40,.16)}
      @media print{
        html,body{background:#fff!important}
        body{padding:0!important}
        .w2b-quote-print-toolbar{display:none!important}
        .w2b-quote-document{width:100%!important;max-width:none!important;min-height:0!important;margin:0!important;padding:0!important;box-shadow:none!important}
      }
    `;

    const html = `<!doctype html>
      <html lang="${escapeHtml(locale)}">
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width,initial-scale=1">
          <title>${escapeHtml(safeFileName)}</title>
          ${printStylesMarkup()}
          <style>${popupCss}</style>
        </head>
        <body ${bodyData}>
          <div class="w2b-quote-print-toolbar" data-print-toolbar>
            <p>Wählen Sie im Druckdialog als Drucker <strong>„Als PDF speichern“</strong>.</p>
            <button type="button" data-print-quote-now>Als PDF speichern / drucken</button>
          </div>
          ${printableDocument.outerHTML}
        </body>
      </html>`;

    try {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      printWindow.close();
      throw new Error('Das Angebotsfenster konnte nicht aufgebaut werden. Bitte laden Sie die Seite neu und versuchen Sie es erneut.');
    }

    const manualPrintButton = printWindow.document.querySelector('[data-print-quote-now]');
    manualPrintButton?.addEventListener('click', () => {
      printWindow.focus();
      printWindow.print();
    });

    waitForPrintAssets(printWindow).then(() => {
      if (printWindow.closed) return;
      printWindow.focus();
      try {
        printWindow.print();
      } catch (error) {
        // Das Dokument bleibt geöffnet und bietet den manuellen Druckbutton an.
        console.error('[W2B Quote Builder] Automatischer Druckdialog konnte nicht geöffnet werden.', error);
      }
    });

    return generatedQuoteNumber;
  }

  function bindQuoteBuilder(builder) {
    const doc = document.querySelector('[data-quote-document]');
    const previewButton = builder.querySelector('[data-create-quote]');
    const saveButton = builder.querySelector('[data-save-quote]');
    if (!doc || !previewButton || builder.dataset.bound === 'true') return;
    builder.dataset.bound = 'true';

    previewButton.addEventListener('click', () => {
      const fields = readQuoteFields(builder);
      setQuoteMessage(builder, '');
      if (!validateQuoteFields(builder, fields)) return;

      try {
        openQuotePrintWindow(doc, fields);
        setQuoteMessage(builder, 'Der Angebotsentwurf wurde in einem neuen Fenster geöffnet. Dort können Sie ihn als PDF speichern oder drucken.', 'success');
      } catch (error) {
        console.error('[W2B Quote Builder]', error);
        setQuoteMessage(builder, error.message || 'Der PDF-Entwurf konnte nicht geöffnet werden.');
        return;
      }

      // Mengen und Kundendaten anschließend im Hintergrund sichern. Die PDF nutzt
      // bereits die aktuell sichtbaren Eingabewerte und ist davon nicht abhängig.
      Promise.resolve()
        .then(() => syncCartQuantities())
        .then(() => persistQuoteAttributes(fields))
        .catch((error) => {
          console.error('[W2B Quote Builder]', error);
          setQuoteMessage(builder, 'Der PDF-Entwurf wurde geöffnet. Die eingegebenen Angebotsdaten konnten jedoch nicht im Warenkorb gespeichert werden.');
        });
    });

    saveButton?.addEventListener('click', async () => {
      const fields = readQuoteFields(builder);
      setQuoteMessage(builder, '');
      if (!validateQuoteFields(builder, fields)) return;
      setButtonsBusy(builder, true);
      try {
        await syncCartQuantities();
        await persistQuoteAttributes(fields);
        const result = await saveQuote(builder, doc, fields);
        fillDocument(doc, fields, result.quoteNumber);
        await persistQuoteAttributes(fields, {
          'Angebotsnummer': result.quoteNumber,
          'Angebotsstatus': 'Im Shopify-Admin gespeichert',
          'Shopify Bestellentwurf': result.draftOrderName || '',
        });
        const reference = result.draftOrderName ? ` (${result.draftOrderName})` : '';
        setQuoteMessage(builder, `Ihre Angebotsanfrage ${result.quoteNumber}${reference} wurde erfolgreich übermittelt und gespeichert.`, 'success');
      } catch (error) {
        setQuoteMessage(builder, error.message || 'Das Angebot konnte nicht gespeichert werden.');
      } finally {
        setButtonsBusy(builder, false);
      }
    });

    builder.querySelectorAll('input, textarea').forEach((field) => {
      field.addEventListener('input', () => setQuoteMessage(builder, ''));
    });
  }

  function initQuoteBuilder() {
    document.querySelectorAll('[data-quote-builder]').forEach(bindQuoteBuilder);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initQuoteBuilder);
  else initQuoteBuilder();
  document.addEventListener('shopify:section:load', initQuoteBuilder);
})();
